/**
 * operator-copy.ts — T320 / FR-017 / FR-018.
 *
 * Centralized Spanish operator labels for the wired editorial, schedules,
 * and delivery surfaces. The glossary grep-gate (scripts/glossary-grep-gate.mjs)
 * scans every JSX text node and allowlisted JSX attribute under
 * src/screens/** and src/components/** for forbidden engineering terms
 * (port_sources_and_glossary.md §4). Centralizing the copy here makes the
 * gate's source of truth single and inspectable: any change to operator
 * vocabulary lives in one file.
 *
 * Invariant (mirroring frontend-v2/src/lib/labels.ts line 12):
 *   NO value may equal its engineering key. Every entry is a paraphrased
 *   operator-facing string, never a leaked technical term.
 *
 * Glossary-clean by construction: every term below was chosen to AVOID the
 * forbidden seeds in tests/fixtures/glossary.es.ts and tests/fixtures/glossary.en.ts.
 * In particular, we DO NOT use:
 *   - "endpoint", "webhook", "backend", "frontend", "HMAC", "JWT"
 *   - "drenar", "flota", "redepliegue", "nodo de borde", "tiempo de ejecución"
 *   - "credencial de transmisión", "rotar credencial de transmisión"
 *   - "stub", "TODO", "placeholder", "fake", "mock", "not wired"
 *
 * Where the underlying backend operation is genuinely fleet-management /
 * edge-drain / redeploy / key-rotation, we surface the OPERATOR meaning
 * ("punto de entrega", "salida programada", "renovar acceso") rather than
 * the engineering shape.
 *
 * Consumers: src/screens/editorial/* and src/screens/delivery/*. Other
 * screens may import from this module as they de-mock; the file is the
 * canonical source of operator-facing copy for the wired slice.
 */

// ─── Editorial inbox ───────────────────────────────────────────────────────

export const EDITORIAL_INBOX_COPY = {
  pageTitle: 'Bandeja editorial',
  pageDescription:
    'Revisa el material recibido desde fuentes conectadas antes de incorporarlo a la biblioteca.',
  emptyTitle: 'Bandeja vacía',
  emptyBody:
    'No hay material pendiente de revisión. Cuando una fuente conectada envíe contenido, aparecerá aquí.',
  searchPlaceholder: 'Buscar por título o fuente',
  filterAll: 'Todos',
  filterPending: 'Pendiente',
  filterAccepted: 'Aceptado',
  filterRejected: 'Rechazado',
  actionReview: 'Revisar',
  actionAccept: 'Aceptar',
  actionReject: 'Rechazar',
  toastAccepted: 'Material aceptado e incorporado a la biblioteca.',
  toastRejected: 'Material rechazado.',
  loadingLabel: 'Cargando bandeja…',
} as const;

// ─── Editorial sync (publish workflow runs) ───────────────────────────────

export const EDITORIAL_SYNC_COPY = {
  pageTitle: 'Estado del flujo editorial',
  pageDescription:
    'Resumen reciente de las publicaciones aplicadas y las que están reintentando.',
  freshnessMessage:
    'El estado del flujo editorial puede ir ligeramente atrasado respecto al servicio.',
  panelRunsTitle: 'Aplicaciones recientes',
  panelPublishesTitle: 'Publicaciones recientes',
  emptyRunsTitle: 'Sin aplicaciones recientes',
  emptyRunsBody:
    'Cuando una publicación se aplique al servicio, aparecerá aquí con su hora y resultado.',
  emptyPublishesTitle: 'Sin publicaciones recientes',
  emptyPublishesBody:
    'Cuando se aprueben publicaciones desde la biblioteca, aparecerán aquí.',
  columnStartedAt: 'Inicio',
  columnReference: 'Referencia',
  columnOperation: 'Operación',
  columnState: 'Estado',
  columnDuration: 'Duración',
} as const;

// ─── Editorial item review (single asset) ────────────────────────────────

export const EDITORIAL_REVIEW_COPY = {
  breadcrumbBack: 'Bandeja editorial',
  pageTitle: 'Revisión de material',
  notFoundTitle: 'Material no encontrado',
  notFoundBody:
    'No pudimos cargar este material. Puede haber sido aceptado, rechazado o eliminado.',
  loadingLabel: 'Cargando material…',
  commentLabel: 'Comentario de revisión',
  commentPlaceholder:
    'Anota observaciones que queden con la decisión editorial.',
  actionAccept: 'Aceptar e incorporar',
  actionReject: 'Rechazar',
  toastAccepted: 'Material aceptado e incorporado a la biblioteca.',
  toastRejected: 'Material rechazado.',
} as const;

// ─── Delivery operations ──────────────────────────────────────────────────

export const DELIVERY_OPS_COPY = {
  pageTitle: 'Operaciones de entrega',
  pageDescription:
    'Acciones operativas sobre los puntos de entrega y las cuentas. Cada acción se aplica en producción, requiere confirmación escrita y queda registrada.',
  warningProduction:
    'Estas acciones afectan la entrega en producción. No se ejecutan de forma optimista: el resultado se confirma contra el servicio antes de marcarse como aplicado.',
  statOnlineLabel: 'Puntos en línea',
  statCapacityLabel: 'Capacidad en uso',
  statOpsTodayLabel: 'Acciones aplicadas hoy',
  statLastOpLabel: 'Última acción',
  panelDeliveryTitle: 'Puntos de entrega',
  panelAccountsTitle: 'Cuentas y acceso',
  panelLogTitle: 'Registro de acciones recientes',
  emptyNodesTitle: 'Sin puntos de entrega',
  emptyNodesBody:
    'No hay puntos de entrega registrados. Comunícate con tu administrador para añadir uno.',
  emptyLogTitle: 'Sin acciones recientes',
  emptyLogBody:
    'Cuando se ejecute una acción operativa, aparecerá aquí con su hora y resultado.',
  loadingLabel: 'Cargando puntos de entrega…',
  statusOnline: 'En línea',
  statusOffline: 'Fuera de línea',
  statusDegraded: 'Con incidencia',
  // Operator-facing action labels — written to AVOID the engineering
  // shape ("drenar", "redepliegue", "rotar credencial de transmisión")
  // documented as forbidden in port_sources §4.3.
  actionVacate: 'Vaciar punto de entrega',
  actionVacateDescription:
    'Reasigna las salidas a otros puntos antes de retirarlo del servicio.',
  actionRestart: 'Reiniciar servicio del punto',
  actionRestartDescription:
    'Reinicia el servicio del punto seleccionado para aplicar cambios recientes.',
  actionFleetStatus: 'Estado consolidado',
  actionFleetStatusDescription:
    'Consulta el estado actual de todos los puntos de entrega.',
  actionRotateAccess: 'Renovar acceso de canal',
  actionRotateAccessDescription:
    'Emite un nuevo acceso de entrada para un canal y desactiva el anterior.',
  actionDisableUser: 'Suspender acceso de un miembro',
  actionDisableUserDescription:
    'Suspende inmediatamente el acceso de un miembro del espacio.',
  actionChangeRole: 'Cambiar rol de un miembro',
  actionChangeRoleDescription:
    'Eleva o reduce el rol de un miembro del espacio.',
  confirmModalDoubleCheck: 'Escribe el nombre del punto para confirmar',
  confirmModalApply: 'Aplicar acción',
  confirmModalCancel: 'Cancelar',
  resultStateOk: 'Aplicada',
  resultStateFailed: 'No aplicada',
} as const;

// ─── Shared common labels ────────────────────────────────────────────────

export const COMMON_COPY = {
  loadingGeneric: 'Cargando…',
  errorGeneric: 'No se pudo cargar la información.',
  retry: 'Reintentar',
  cancel: 'Cancelar',
  apply: 'Aplicar',
  save: 'Guardar',
  close: 'Cerrar',
  notAssigned: 'Sin asignar',
  pending: 'Pendiente',
  applied: 'Aplicado',
  failed: 'Con fallo',
} as const;

// ─── Type exports (for consumers that want narrow keys) ──────────────────

export type EditorialInboxCopyKey = keyof typeof EDITORIAL_INBOX_COPY;
export type EditorialSyncCopyKey = keyof typeof EDITORIAL_SYNC_COPY;
export type EditorialReviewCopyKey = keyof typeof EDITORIAL_REVIEW_COPY;
export type DeliveryOpsCopyKey = keyof typeof DELIVERY_OPS_COPY;
export type CommonCopyKey = keyof typeof COMMON_COPY;
